<script setup>
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import TdfDashboard from './TdfDashboard.vue';
import TdfReception from './TdfReception.vue';
import TdfReservations from './TdfReservations.vue';
import TdfDirectSales from './TdfDirectSales.vue';
import TdfClients from './TdfClients.vue';
import TdfAdmin from './TdfAdmin.vue';
import TdfOtherRoutines from './TdfOtherRoutines.vue';
import TdfConfig from './TdfConfig.vue';
import TdfUnits from './TdfUnits.vue';

const props = defineProps({
  activeTab: {
    type: String,
    default: 'dashboard',
  },
});

const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const tabs = computed(() => [
  {
    key: 'dashboard',
    label: t('TDF.TABS.DASHBOARD'),
    routeName: 'tdf_dashboard',
  },
  {
    key: 'reception',
    label: t('TDF.TABS.RECEPTION'),
    routeName: 'tdf_reception',
  },
  {
    key: 'reservations',
    label: t('TDF.TABS.RESERVATIONS'),
    routeName: 'tdf_reservations',
  },
  {
    key: 'direct-sales',
    label: t('TDF.TABS.DIRECT_SALES'),
    routeName: 'tdf_direct_sales',
  },
  {
    key: 'clients',
    label: t('TDF.TABS.CLIENTS'),
    routeName: 'tdf_clients',
  },
  {
    key: 'admin',
    label: t('TDF.TABS.ADMIN'),
    routeName: 'tdf_admin',
  },
  {
    key: 'other-routines',
    label: t('TDF.TABS.OTHER_ROUTINES'),
    routeName: 'tdf_other_routines',
  },
  {
    key: 'units',
    label: t('TDF.TABS.UNITS'),
    routeName: 'tdf_units',
  },
  {
    key: 'config',
    label: t('TDF.TABS.CONFIG'),
    routeName: 'tdf_config',
  },
]);

const componentMap = {
  dashboard: TdfDashboard,
  reception: TdfReception,
  reservations: TdfReservations,
  'direct-sales': TdfDirectSales,
  clients: TdfClients,
  admin: TdfAdmin,
  'other-routines': TdfOtherRoutines,
  units: TdfUnits,
  config: TdfConfig,
};

const currentComponent = computed(
  () => componentMap[props.activeTab] || TdfDashboard
);
</script>

<template>
  <SettingsLayout>
    <template #header>
      <BaseSettingsHeader
        :title="t('TDF.HEADER')"
        :description="t('TDF.DESCRIPTION')"
      />
    </template>
    <template #default>
      <div class="flex flex-col gap-4">
        <div
          class="flex gap-1 overflow-x-auto border-b border-slate-100 dark:border-slate-700"
        >
          <router-link
            v-for="tab in tabs"
            :key="tab.key"
            :to="accountScopedRoute(tab.routeName)"
            class="px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap"
            :class="
              activeTab === tab.key
                ? 'border-woot-500 text-woot-500'
                : 'border-transparent text-slate-600 dark:text-slate-300 hover:text-woot-500 hover:border-slate-300'
            "
          >
            {{ tab.label }}
          </router-link>
        </div>
        <component :is="currentComponent" />
      </div>
    </template>
  </SettingsLayout>
</template>
